import { CHEVRON_LEFT_16 } from "@sznds/icons";
import Header from "~/ui/header";
import createHeaderButton from "~/ui/headerbutton";
import { addListener, removeListener } from "~/lib/signals";
import { store } from "~/state/store";
import { watch, watchCurrentUser } from "~/state/util";
import ActivitiesListItem from "./list-item";
import Filter from "./filter";
import { _ } from "~/i18n";
import createMobileAppButtons from "~/ui/mobileappbuttons";
import Scene from "~/scene";
import Lookup from "~/activities/lookup";
import { setCoordinates } from "~/state/map/state";
import ControlPointer from "~/ui/controls/pointer";
import mapControls from "~/scene/control/mapcontrols";
import { getPointerCenterPx } from "~/util/pointer";
import { node } from "~/util/dom";
import MyMapsLazyLoad from "~/es6/mymaps.lazyload";
import { Unsubscribe } from "redux";
import createSDSIcon from "~/ui/icon";
import { ELoginState } from "~/state/user/interfaces";
import { activityTypeToIcon } from "./util";

export function setSEO() {
	Mapy.SEO.set(_("mymap.switcher.activities"), _("mymap.activity.myActivitiesDesc2"));
}

export default class ActivitiesList {
	_drop: ControlPointer;

	_items: Array<any>;

	_dom: {
		container: HTMLDivElement;
		list: HTMLDivElement;
		ul: HTMLUListElement;
		loader: HTMLParagraphElement;
	};

	_filter: Filter;

	_active: boolean;

	_show: boolean;

	_zoomToItems: boolean;

	_page: number;

	_lazyLoad: MyMapsLazyLoad;

	_highlightListener: string;

	_lookup: Lookup;

	_storeUnsubscribe: Unsubscribe;

	_header: Header;

	constructor() {
		this._items = [];
		this._dom = {
			container: node("div"),
			list: node("div", { className: "activities-list" }),
			ul: node("ul"),
			loader: node("p", { innerHTML: _("mymap.activity.loading"), className: "loading" }),
		};
		this._buildHeader();
		this._filter = null;
		this._active = false;
		this._show = false;
		this._zoomToItems = false;
		this._page = 0;
		this._highlightListener = null;
		this._lookup = null;

		this._lazyLoad = new MyMapsLazyLoad({
			testSelector: ".activities-list",
			cb: () => {
				this._nextPage();
			},
		});

		this._drop = new ControlPointer({
			type: "red",
		});

		mapControls.addControlPointer(this._drop);

		// deaktivace rucniho mereni a vlastnich bodu
		addListener(this, "distacemeter-activated", () => this._disableInteraction());
		addListener(this, "distacemeter-deactivated", () => this._enableInteraction());
		addListener(this, "usermarks-activated", () => this._disableInteraction());
		addListener(this, "usermarks-deactivated", () => this._enableInteraction());
		addListener(this, "print-page", () => this._printPage());
		addListener(this, "print-done", () => this._printDone());

		addListener(this, "gpx-activated", () => {
			if (this._active) {
				this._lookup?.stop();
			}
		});

		addListener(this, "gpx-deactivated", () => {
			if (this._active) {
				this._lookup?.start();
			}
		});

		const watcher = watch(store.getState, "user", watchCurrentUser);

		this._storeUnsubscribe = store.subscribe(watcher(newUser => {
			if (newUser.user.userPublicId && newUser.user.state === ELoginState.Green) {
				this._header.avatar = newUser.user.avatar;
				this._header.premium = newUser.user.premium ? "mapy" : "none";
			}
		}));
	}

	activate() {
		if (this._active) {
			return;
		}

		if (!this._filter) {
			this._filter = new Filter(this);
		}

		if (!this._lookup) {
			this._lookup = new Lookup(this);

			this._show && this._lookup.start();
		}

		this._dom.container.appendChild(this._filter.container);
		this._dom.container.appendChild(this._dom.list);

		this.show();

		this._addLoader();
		this._clearItems();
		this.items = this._filter.getItems();

		this._active = true;
	}

	deactivate() {
		if (!this._active) {
			return;
		}

		if (this._filter) {
			this._filter.clear();
		}

		this._dom.list.innerHTML = "";
		this._dom.ul.innerHTML = "";
		this.hide();
		this._page = 0;
		this._active = false;
	}

	show() {
		if (this._show) {
			return;
		}

		setSEO();
		this._lazyLoad.enabled = true;
		this._lazyLoad.container = Mapy.getComponent("mymaps").getContainer();
		Mapy.getComponent("activities").setSEO();
		this._highlightListener = addListener(this, "highlight-activities-list-item", "_showDrop");
		this._show = true;

		this._lookup?.start();

		Scene.getContainer().addEventListener("photo-gallery-close", this);
		Scene.getContainer().addEventListener("photo-detail-close", this);
	}

	hide() {
		if (!this._show) {
			return;
		}
		this._lazyLoad.enabled = false;
		removeListener(this._highlightListener);
		this._highlightListener = null;
		this._show = false;

		this._lookup?.stop();

		Scene.getContainer().removeEventListener("photo-gallery-close", this);
		Scene.getContainer().removeEventListener("photo-detail-close", this);
	}

	get container() {
		return this._dom.container;
	}

	// eslint-disable-next-line class-methods-use-this
	get state() {
		return {};
	}

	set state(state) {
		this._lookup?.openDetail();
	}

	get visible() {
		return this._show;
	}

	set items(promise) {
		promise.then(items => {
			this._responseItems(items);
			this._removeLoader();
		}, () => {
			this._removeLoader();
		});
	}

	get page() {
		return this._page;
	}

	set season(season) {
		this._filter.season = season;
	}

	set filter(obj) {
		this._filter.filter = obj;
	}

	get filter() {
		return this._filter.filter;
	}

	set zoomToItems(zoomToItems) {
		this._zoomToItems = zoomToItems;
	}

	getItems(dateFrom, dateTo, type) {
		this.filter = { dateFrom, dateTo, type };
		this.loadData();
	}

	async loadData(summary?, clear?) {
		const tmpPage = this._page;

		if (clear) {
			this.filter = { loadItems: this._items.length };
			this._clearItems();
			this._lookup?.lookup();
			this._lookup?.highlight();
			this.highlight();
		}

		if (summary) {
			await this._filter.loadSummary();
		}

		this.items = this._filter ? this._filter.getItems() : Promise.resolve([]);

		this._page = tmpPage;
		this._lookup.lookup();
	}

	clear() {
		if (this._filter) {
			this._filter.clear();
		}
	}

	changeFilter() {
		if (this._lazyLoad) {
			this._lazyLoad.enabled = true;
		}

		this._clearItems();
	}

	highlight(id?) {
		this._lookup?.highlight(id);

		for (let index = 0; index < this._items.length; index++) {
			this._items[index].highlight(id && this._items[index].id === id);
		}
	}

	open(id) {
		this.highlight();

		this._lookup?.openDetail(id);

		Mapy.getComponent("activities").state = {
			mid: id,
			backTitle: _("back"),
			backAction: () => {
				Mapy.getComponent("activities").state = {};
				Mapy.getComponent("mymaps").activeComponent(Mapy.getComponent("activities"));
				this._lookup?.lookup();
				Mapy.getComponent("history").save();
			},
		};
	}

	handleEvent(event) {
		if (event.type === "photo-detail-close" || event.type === "photo-gallery-close") {
			this._active && !Mapy.getComponent("gallery").isActive() && setSEO();
		}
	}

	_buildHeader() {
		const { user } = store.getState().user;

		this._header = new Header({
			title: _("mymap.switcher.activities"),
			avatar: user.avatar,
			premium: user.premium ? "mapy" : "none",
		});
		this._dom.container.appendChild(this._header.node);

		const back = createHeaderButton({
			icon: CHEVRON_LEFT_16,
			innerHTML: _("mymap.stateswitch"),
		});

		back.addEventListener("click", () => {
			Mapy.getComponent("mymaps").activeComponent(Mapy.getComponent("dashboard"));
			Mapy.getComponent("history").save();
		});
		back.setAttribute("data-log-text", "backToDashboard");

		this._header.leftButtons.appendChild(back);
	}

	_buildEmpty() {
		this._dom.list.innerHTML = "";
		this._dom.ul.innerHTML = "";

		if ((this._filter && this._filter.totalCount === 0) || !this._filter) {
			const desc = node("p", { innerHTML: _("mymap.activity.empty.desc") });
			const apps = createMobileAppButtons();

			this._dom.list.appendChild(desc);
			this._dom.list.appendChild(apps);
		} else {
			const desc = node("p", { textContent: _("mymap.activity.notify.noFilteredItems") });

			this._dom.list.appendChild(desc);
		}

		this._dom.list.classList.add("empty");
	}

	_responseItems(items) {
		if (this._items.length === 0 && items.length === 0) {
			Mapy.getComponent("mymaps").getContainer().classList.add("empty");
			this._buildEmpty();
		} else if (items.length > 0) {
			this._dom.list.classList.remove("empty");
			Mapy.getComponent("mymaps").getContainer().classList.remove("empty");
			this._buildItems(items);

			if (this._zoomToItems) {
				this._zoomToItems = false;

				const coords = this._items.filter(item => item.coords[0]).map(item => item.coords[0]);

				if (coords.length && this.visible) {
					const centerZoom = Scene.computeCenterZoom(coords, true);
					const [longitude, latitude] = centerZoom[0].toWGS84();

					store.dispatch(setCoordinates({
						longitude,
						latitude,
						...centerZoom[1] === Infinity ? {} : { zoom: centerZoom[1] },
						animate: true,
					}));
				}
			} else {
				this._lookup?.lookup();
			}
		}
	}

	_buildItems(items) {
		items.forEach(item => {
			const li = new ActivitiesListItem(item, this);

			this._dom.ul.appendChild(li.container);
			this._items.push(li);
		});

		this._dom.list.appendChild(this._dom.ul);
	}

	_clearItems() {
		this._dom.list.innerHTML = "";
		this._dom.ul.innerHTML = "";
		this._page = 0;

		if (this._items.length) {
			while (this._items.length) {
				const item = this._items.shift();

				item.destroy();
			}
		}

		this._lookup.clear();
	}

	_addLoader() {
		this._dom.container.appendChild(Mapy.getComponent("loader").getBigCon());
		Mapy.getComponent("loader").start(true);
	}

	_removeLoader() {
		Mapy.getComponent("loader").end(true);
		if (Mapy.getComponent("loader").getBigCon().parentNode === this._dom.container) {
			Mapy.getComponent("loader").getBigCon().remove();
		}

		this._dom.loader.remove();
	}

	_nextPage() {
		if (this._filter) {
			this._filter.preventAnimation = true;
		}

		this._page++;
		this._dom.list.appendChild(this._dom.loader);
		this.loadData();
	}

	_showDrop(data) {
		this._drop.setPosition(data.data.show
			? () => ({
				position: getPointerCenterPx(data.data.coords),
			})
			: null);
	}

	_disableInteraction() {
		this.highlight({});
		this._lookup?.disableInteraction();
	}

	_enableInteraction() {
		this.highlight();
		this._lookup?.enableInteraction();
	}

	_printPage() {
		if (!this._active) {
			return;
		}

		// hlavicka
		const header = node("div", { className: "info-header" });

		header.appendChild(node("p", { className: "title", textContent: _("mymap.activity.titleMy") }));
		header.appendChild(node("p", { className: "date", textContent: `${_("mymap.activity.printActivity")} ${this._filter.infoDate}` }));

		const { type } = this._filter.filter;

		if (type !== "all") {
			header.insertBefore(createSDSIcon({ symbol: activityTypeToIcon(type) }), header.firstChild);
		}

		this._dom.container.insertBefore(header, this._dom.container.firstChild);
		this._items.forEach(item => item.print());
	}

	_printDone() {
		if (!this._active) {
			return;
		}

		Array.from(this._dom.container.querySelectorAll(".info-header")).forEach(el => {
			el.remove();
		});

		this._items.forEach(item => item.printDone());
	}
}
